import React, { useState } from "react";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { FeatureCheck } from "./ui/feature-check";

enum MembershipPlan {
  Standard = "Standard",
  Pro = "Pro",
  ProPlus = "Pro Plus",
}

interface MembershipPlanDetailsProps {
  children?: React.ReactNode;
}

export const MembershipPlanDetails: React.FC<MembershipPlanDetailsProps> = ({
  children,
}) => {
  const [expandedDetails, setExpandedDetails] = useState(false);

  return (
    <div>
      <Button
        variant="secondary"
        className="mb-4 flex w-full items-center justify-center gap-2"
        onClick={() => setExpandedDetails(!expandedDetails)}
      >
        {expandedDetails
          ? "Hide Detailed Comparison"
          : "Show Detailed Comparison"}
        {expandedDetails ? <ChevronUp /> : <ChevronDown />}
      </Button>
      {expandedDetails && (
        <Card className="mt-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="p-4 text-left">Feature</th>
                {Object.values(MembershipPlan).map((plan) => (
                  <th key={plan} className="p-4 text-center">
                    {plan}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-gray-50">
                <td colSpan={4} className="p-4 font-bold">
                  Profiles & Visibility
                </td>
              </tr>
              {[
                {
                  name: "Basic Profile (1 Coaching Service)",
                  values: {
                    [MembershipPlan.Standard]: true,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Expanded Profile with Video",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Company Logo and Branding",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Landing Page with VSL",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: "1 page",
                    [MembershipPlan.ProPlus]: "Unlimited",
                  },
                },
                {
                  name: "Multiple Coaching Offers",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: false,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Client Testimonials",
                  values: {
                    [MembershipPlan.Standard]: "Up to 3",
                    [MembershipPlan.Pro]: "Unlimited",
                    [MembershipPlan.ProPlus]: "Unlimited",
                  },
                },
              ].map((feature) => (
                <tr key={feature.name} className="border-b">
                  <td className="p-4">{feature.name}</td>
                  {Object.values(MembershipPlan).map((plan) => (
                    <td key={plan} className="p-4 text-center">
                      {"boolean" === typeof feature.values[plan] ? (
                        feature.values[plan] ? (
                          <FeatureCheck className="mx-auto" />
                        ) : (
                          <X className="mx-auto text-gray-300" />
                        )
                      ) : (
                        <span>{feature.values[plan]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-b bg-gray-50">
                <td colSpan={4} className="p-4 font-bold">
                  Content & Marketing Tools
                </td>
              </tr>
              {[
                {
                  name: "Share Content Pieces",
                  values: {
                    [MembershipPlan.Standard]: "Up to 2",
                    [MembershipPlan.Pro]: "Unlimited",
                    [MembershipPlan.ProPlus]: "Unlimited",
                  },
                },
                {
                  name: "Lead Magnets",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "CRM Contacts",
                  values: {
                    [MembershipPlan.Standard]: "50",
                    [MembershipPlan.Pro]: "1,000",
                    [MembershipPlan.ProPlus]: "Unlimited",
                  },
                },
                {
                  name: "Follow-ups (Email)",
                  values: {
                    [MembershipPlan.Standard]: true,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Texting with Prospects",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Advanced Workflows",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: false,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
              ].map((feature) => (
                <tr key={feature.name} className="border-b">
                  <td className="p-4">{feature.name}</td>
                  {Object.values(MembershipPlan).map((plan) => (
                    <td key={plan} className="p-4 text-center">
                      {"boolean" === typeof feature.values[plan] ? (
                        feature.values[plan] ? (
                          <FeatureCheck className="mx-auto" />
                        ) : (
                          <X className="mx-auto text-gray-300" />
                        )
                      ) : (
                        <span>{feature.values[plan]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-b bg-gray-50">
                <td colSpan={4} className="p-4 font-bold">
                  Booking & Client Engagement
                </td>
              </tr>
              {[
                {
                  name: "Integrated Calendar",
                  values: {
                    [MembershipPlan.Standard]: true,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Booking Discovery Calls",
                  values: {
                    [MembershipPlan.Standard]: true,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Integrated Payments",
                  values: {
                    [MembershipPlan.Standard]: true,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Host & Sell Courses",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: true,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
                {
                  name: "Create Communities",
                  values: {
                    [MembershipPlan.Standard]: false,
                    [MembershipPlan.Pro]: false,
                    [MembershipPlan.ProPlus]: true,
                  },
                },
              ].map((feature) => (
                <tr key={feature.name} className="border-b">
                  <td className="p-4">{feature.name}</td>
                  {Object.values(MembershipPlan).map((plan) => (
                    <td key={plan} className="p-4 text-center">
                      {"boolean" === typeof feature.values[plan] ? (
                        feature.values[plan] ? (
                          <FeatureCheck className="mx-auto" />
                        ) : (
                          <X className="mx-auto text-gray-300" />
                        )
                      ) : (
                        <span>{feature.values[plan]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {children}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};
